import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { Mail, Phone, Menu, X, Moon, Sun, ArrowRight } from 'lucide-react';

export const MARKETING_PAGES = [
  { to: '/harga', label: 'Harga' },
  { to: '/ai-customer-service', label: 'AI Customer Service' },
  { to: '/ai-whatsapp', label: 'AI WhatsApp' },
  { to: '/crm-pesanan', label: 'CRM & Pesanan' },
  { to: '/tentang-kami', label: 'Tentang Kami' },
];

export const MarketingShell = ({ children, active }) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <div className={`min-h-screen overflow-x-hidden ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors overflow-x-hidden">
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-[#0A0A0A]/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2" data-testid="marketing-logo-link">
                <img src="/images/logo.png" alt="Nerava" className="w-8 h-8" />
                <span className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>Nerava</span>
              </Link>

              <nav className="hidden lg:flex items-center gap-7">
                {MARKETING_PAGES.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    data-testid={`marketing-nav-${p.to.replace('/', '')}`}
                    className={`text-sm transition-colors ${active === p.to ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-slate-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400'}`}
                  >
                    {p.label}
                  </Link>
                ))}
              </nav>

              <div className="flex items-center gap-2 sm:gap-3">
                {user ? (
                  <Link to="/dashboard">
                    <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-4 sm:px-6 text-xs sm:text-sm">Dashboard</Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/login" className="hidden sm:block">
                      <Button variant="ghost" className="text-slate-600 dark:text-zinc-300">Masuk</Button>
                    </Link>
                    <Link to="/register">
                      <Button data-testid="marketing-cta-register" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-3 sm:px-6 text-xs sm:text-sm whitespace-nowrap">
                        <span className="hidden sm:inline">Coba Gratis 30 Hari</span>
                        <span className="sm:hidden">Mulai</span>
                      </Button>
                    </Link>
                  </>
                )}
                <button
                  onClick={toggleTheme}
                  data-testid="marketing-theme-toggle"
                  aria-label={theme === 'dark' ? 'Aktifkan mode terang' : 'Aktifkan mode gelap'}
                  className="flex-shrink-0 grid place-items-center w-9 h-9 rounded-full border border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-500/60 hover:rotate-12 transition-[color,border-color,transform] duration-300"
                >
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
                <button onClick={() => setMenuOpen(!menuOpen)} data-testid="marketing-menu-button" aria-label="Menu" className="lg:hidden p-2 flex-shrink-0 text-slate-700 dark:text-zinc-200">
                  {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>

            {menuOpen && (
              <div className="lg:hidden -mx-4 mt-4 px-4 pb-4 border-t border-slate-200 dark:border-zinc-800 pt-4 bg-white dark:bg-[#0A0A0A]">
                <nav className="flex flex-col gap-3">
                  {MARKETING_PAGES.map((p) => (
                    <Link key={p.to} to={p.to} className="text-slate-600 dark:text-zinc-300 py-2" onClick={() => setMenuOpen(false)}>
                      {p.label}
                    </Link>
                  ))}
                </nav>
              </div>
            )}
          </div>
        </header>

        <main className="pt-20">{children}</main>

        <footer className="py-14 px-4 bg-zinc-950 text-zinc-400 border-t border-zinc-900 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-8">
              <div className="col-span-2 md:col-span-1">
                <Link to="/" className="flex items-center gap-2 mb-4">
                  <img src="/images/logo.png" alt="Nerava" className="w-10 h-10 flex-shrink-0" />
                  <span className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Nerava</span>
                </Link>
                <p className="text-sm">
                  CRM omnichannel dengan AI customer assistant untuk toko online dan UMKM Indonesia.
                </p>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white mb-4">Produk</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/ai-customer-service" className="hover:text-indigo-400 transition-colors">AI Customer Service</Link></li>
                  <li><Link to="/ai-whatsapp" className="hover:text-indigo-400 transition-colors">AI WhatsApp</Link></li>
                  <li><Link to="/crm-pesanan" className="hover:text-indigo-400 transition-colors">CRM & Pesanan</Link></li>
                  <li><Link to="/harga" className="hover:text-indigo-400 transition-colors">Harga</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white mb-4">Perusahaan</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/tentang-kami" className="hover:text-indigo-400 transition-colors">Tentang Kami</Link></li>
                  <li><Link to="/blog" className="hover:text-indigo-400 transition-colors">Blog</Link></li>
                  <li><Link to="/privacy-policy" className="hover:text-indigo-400 transition-colors">Kebijakan Privasi</Link></li>
                  <li><Link to="/terms" className="hover:text-indigo-400 transition-colors">Syarat & Ketentuan</Link></li>
                </ul>
              </div>
              <div className="col-span-2 md:col-span-1">
                <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-white mb-4">Kontak</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a href="mailto:support@nerava.id" className="flex items-center gap-2 hover:text-indigo-400 transition-colors">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="break-all">support@nerava.id</span>
                    </a>
                  </li>
                  <li>
                    <a href="tel:+6285121288598" className="flex items-center gap-2 hover:text-indigo-400 transition-colors">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      +62 851-2128-8598
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="border-t border-zinc-900 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left">
              <p className="text-sm">© 2026 Nerava. All rights reserved.</p>
              <div className="flex gap-4 sm:gap-6 text-sm">
                <Link to="/privacy-policy" className="hover:text-indigo-400 transition-colors">Privasi</Link>
                <Link to="/terms" className="hover:text-indigo-400 transition-colors">Ketentuan</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export const PageHero = ({ eyebrow, title, highlight, subtitle, bullets = [], primaryCta = 'Coba Gratis 30 Hari' }) => (
  <section className="relative px-4 pt-14 pb-16 sm:pt-20 sm:pb-20 bg-white dark:bg-[#0A0A0A] overflow-hidden">
    <div className="absolute -top-24 -left-24 w-[26rem] h-[26rem] rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />
    <div className="relative">
      <div className="max-w-7xl mx-auto">
        {eyebrow && (
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-300 mb-6">
            {eyebrow}
          </span>
        )}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-white mb-6 leading-[1.08]" style={{ fontFamily: 'Manrope' }}>
          {title}{' '}
          {highlight && (
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500 dark:from-indigo-400 dark:to-blue-400">{highlight}</span>
          )}
        </h1>
        <p className="text-lg sm:text-xl text-slate-600 dark:text-zinc-300 mb-8 max-w-2xl">{subtitle}</p>
        {bullets.length > 0 && (
          <ul className="flex flex-wrap gap-x-6 gap-y-2 mb-8 text-sm text-slate-500 dark:text-zinc-400">
            {bullets.map((b) => (
              <li key={b} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                {b}
              </li>
            ))}
          </ul>
        )}
        <Link to="/register">
          <Button data-testid="page-hero-cta" className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 py-6 text-base sm:text-lg group shadow-[0_18px_36px_-18px_rgba(79,70,229,0.7)] transition-[background-color,box-shadow,transform] hover:-translate-y-0.5">
            {primaryCta}
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

export const PageSection = ({ id, title, intro, children, tinted = false }) => (
  <section id={id} className={`px-4 py-16 sm:py-20 ${tinted ? 'bg-slate-50 dark:bg-[#0A0A0A]' : 'bg-white dark:bg-[#0A0A0A]'}`}>
    <div className="max-w-7xl mx-auto">
      {title && (
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope' }}>
          {title}
        </h2>
      )}
      {intro && <p className="text-base text-slate-600 dark:text-zinc-300 mb-10 max-w-3xl">{intro}</p>}
      {children}
    </div>
  </section>
);

export const CardGrid = ({ items }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
    {items.map((item) => (
      <div
        key={item.title}
        className="rounded-2xl border border-slate-200 bg-white p-6 transition-[transform,border-color,box-shadow] hover:-translate-y-1 hover:border-indigo-300 hover:shadow-[0_18px_40px_-28px_rgba(79,70,229,0.6)] dark:border-zinc-800 dark:bg-zinc-900/60 dark:hover:border-indigo-500/50"
      >
        {item.icon && <div className="mb-4 grid place-items-center w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">{item.icon}</div>}
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{item.title}</h3>
        <p className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed">{item.description}</p>
      </div>
    ))}
  </div>
);

export const ClosingCta = ({ title, subtitle, showPricingCta = true }) => (
  <section className="px-4 py-16 sm:py-20 bg-white dark:bg-[#0A0A0A]">
    <div className="max-w-7xl mx-auto rounded-3xl bg-gradient-to-br from-indigo-600 to-blue-600 px-6 sm:px-12 py-12 sm:py-16 text-center">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>{title}</h2>
      <p className="text-base text-indigo-100 mb-8 max-w-2xl mx-auto">{subtitle}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/register">
          <Button data-testid="closing-cta-register" className="w-full sm:w-auto bg-white text-indigo-700 hover:bg-indigo-50 rounded-full px-8 py-6 text-base font-semibold">
            Coba Gratis 30 Hari
          </Button>
        </Link>
        {showPricingCta && (
          <Link to="/harga">
            <Button data-testid="closing-cta-pricing" variant="outline" className="w-full sm:w-auto rounded-full px-8 py-6 text-base border-white/60 text-white hover:bg-white/10 bg-transparent">
              Lihat Harga
            </Button>
          </Link>
        )}
      </div>
    </div>
  </section>
);

export const FaqList = ({ items }) => (
  <div className="space-y-4 max-w-3xl">
    {items.map((f) => (
      <div key={f.q} className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900/60">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2">{f.q}</h3>
        <p className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed">{f.a}</p>
      </div>
    ))}
  </div>
);

export const buildFaqJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

export const buildBreadcrumb = (name, path) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Beranda', item: 'https://nerava.id/' },
    { '@type': 'ListItem', position: 2, name, item: `https://nerava.id${path}` },
  ],
});
